import { useEffect, useState } from 'react'
import { MonitorDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * "Install ProTrack on your Desktop".
 * The real PWA install is driven by Chrome/Edge's `beforeinstallprompt` event, which
 * the browser fires only sometimes (fresh load, installable, not already installed,
 * some engagement). When we have that captured prompt, clicking installs a real desktop
 * app icon. When we DON'T, we no longer download a confusing ".url" file — instead we
 * guide the user to the address-bar install icon, which is always available when the
 * app is installable. This keeps the experience consistent (never a stray download).
 */
export default function InstallAppButton({ className = '', label = 'Install ProTrack on your Desktop' }) {
  const { t } = useTranslation('ui')
  const [canInstall, setCanInstall] = useState(typeof window !== 'undefined' && !!window.__deferredInstallPrompt)
  const [installed, setInstalled] = useState(false)
  const [showHint, setShowHint] = useState(false)

  useEffect(() => {
    const onAvail = () => { setCanInstall(true); setShowHint(false) }
    const onDone = () => { setCanInstall(false); setInstalled(true) }
    window.addEventListener('pwa-installable', onAvail)
    window.addEventListener('pwa-installed', onDone)
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) setInstalled(true)
    return () => {
      window.removeEventListener('pwa-installable', onAvail)
      window.removeEventListener('pwa-installed', onDone)
    }
  }, [])

  const handleInstall = async () => {
    const dp = window.__deferredInstallPrompt
    if (dp) {
      dp.prompt()
      try { await dp.userChoice } catch { /* ignore */ }
      window.__deferredInstallPrompt = null
      setCanInstall(false)
      return
    }
    // No captured prompt — guide the user to the browser's own install control
    // instead of downloading a shortcut file.
    setShowHint(true)
  }

  // Already running as an installed app — nothing to offer.
  if (installed) return null

  return (
    <div>
      <button
        type="button"
        onClick={handleInstall}
        className={className || 'w-full flex items-center justify-center gap-2 bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-xl shadow-lg shadow-blue-500/25 transition-all'}
      >
        <MonitorDown className="w-4 h-4" /> {label}
      </button>

      {showHint && (
        <div className="mt-2 relative rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-900">
          <button type="button" onClick={() => setShowHint(false)} className="absolute top-1.5 right-1.5 text-blue-400 hover:text-blue-700" aria-label={t('aria_dismiss')}>
            <X className="w-3.5 h-3.5" />
          </button>
          <p className="font-semibold mb-1">{t('already_installed')}</p>
          <p className="leading-relaxed">
            If you see an <span className="font-semibold">"Open in app"</span> chip in the address bar at the top, ProTrack is <span className="font-semibold">already installed</span> on this computer — click it to open the app, or press the Windows key and type "ProTrack" to find its icon.
          </p>
          <p className="leading-relaxed mt-1.5">
            Not installed yet? Click the <span className="font-semibold">install icon</span> (a small monitor with a down-arrow, or a ⊕) in the address bar, then choose <span className="font-semibold">{t('install')}</span> — ProTrack gets its own window and desktop icon.
          </p>
          <p className="leading-relaxed mt-1 text-blue-700">
            No icon at all? Open the site in Chrome or Edge over https and reload once.
          </p>
        </div>
      )}
    </div>
  )
}
